/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-floating-promises */
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { TreeItem, TreeView } from "@mui/lab/";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { BoxProps } from "@mui/system";
import { usePopup } from "@nodeon/react-components";
import React, { useCallback, useEffect, useState } from "react";
import { Choose, classNames } from "react-extras";
import { useTranslation } from "react-i18next";
import { ReturnNodeDto, useFileApi } from "~/api/GeneratedApi";
import { useAppSelector } from "~/redux/store/store";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { useSorting } from "~/utils/hooks/useSorting";
import CustomBreadcrumbs from "../CustomBreadcrumbs";
import FileUpload from "../FileUpload";
import LoadingBox from "../LoadingBox";
import { useFileUpload } from "../useFileUpload";
import AddFolderPopupForm from "./AddFolderPopupForm";
import FileExplorerFileContextMenu from "./FileExplorerFileContextMenu";
import {
  FileExplorerTableHead,
  SortOrder,
  TableHeader,
} from "./FileExplorerTableHead";
import FileExplorerToolBar from "./FileExplorerToolbar";
import FileExplorerTreeContextMenu from "./FileExplorerTreeContextMenu";
import useFileTranslations from "./useFileTranslations";

interface Props {
  onFileSelected?: (files: ReturnNodeDto[]) => void;
  onClose?: () => void;
  embedded?: boolean;
  disableMultiple?: boolean;
  context?: NodeContext;
}

export type NodeContext =
  | { id: number; name?: never }
  | { id?: never; name: string }
  | { id: number; name?: never; child: NodeContext }
  | { id?: never; name: string; child: NodeContext };

const tableHeaders: TableHeader<ReturnNodeDto>[] = [
  {
    transLationKey: "table-file-name",
    tableCellProps: { sx: { width: "80%" } },
    sortKey: "name",
  },
  {
    transLationKey: "table-file-size",
    tableCellProps: { sx: { width: "20%" }, align: "right" },
    sortKey: "bytes",
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    width: "100%",
    overflowY: "auto",
    whiteSpace: "nowrap",
    overflowX: "hidden",
  },
  treeContainer: {
    borderRight: "1px solid #666",
    height: "100%",
    overflowY: "auto",
  },
  breadcrumbContainer: {
    borderBottom: "1px solid #666",
    paddingLeft: theme.spacing(0.5),
  },
  selectedItem: {
    border: "1px solid #4c4c4c",
    backgroundColor: "rgba(0, 0, 0, 0.08)",
  },
  group: {
    marginLeft: 6,
  },
  nodeContainer: {
    overflowX: "hidden",
    overflowY: "auto",
  },
  nodeContainerMobile: {
    overflowX: "scroll",
    overflowY: "auto",
  },
  node: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
  breadcrumb: {
    "&:hover": {
      textDecoration: "underline",
    },
  },
  labelRoot: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.5, 0),
  },
  labelIcon: {
    marginRight: theme.spacing(1),
    color: "#8f8f8f",
  },
  labelText: {
    fontWeight: "inherit",
    flexGrow: 1,
  },
  stickyHeader: {
    backgroundColor: "#e6e6e6",
    "& th": {
      backgroundColor: "#e6e6e6",
    },
  },
  selectedIndicator: {
    backgroundColor: "rgba(0, 0, 0, 0.08)",
    width: "50%",
    margin: "0 auto",
    marginTop: "auto",
    height: "90%",
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    transition: theme.transitions.create("height", {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut,
    }),
  },
  selectedIndicatorHidden: {
    height: 0,
  },
}));

const initialState = {
  mouseX: null,
  mouseY: null,
};

const FileExplorer = ({
  onFileSelected,
  onClose,
  embedded,
  disableMultiple = false,
  context,
  ...rest
}: Props & BoxProps): JSX.Element => {
  const classes = useStyles();
  const { t } = useTranslation("file-explorer");
  const { translateNodeName } = useFileTranslations();
  const { getComparator } = useSorting();
  const fileApi = useFileApi();

  const [selectedFolder, setSelectedFolder] = useState<string>("1");
  const [nodesExpanded, setNodesExpanded] = useState<string[]>(["1"]);
  const [selectedFiles, setSelectedFiles] = useState<number[]>([]);
  const [isDraggingFile, setIsDraggingFile] = useState<boolean>(false);

  const [contextTreeMenuPosition, setContextTreeMenuPosition] = React.useState<{
    mouseX: null | number;
    mouseY: null | number;
  }>(initialState);
  const [currentTreeContextMenuNode, setCurrentTreeContextMenuNode] =
    useState<ReturnNodeDto>();

  const [contextFileMenuPosition, setContextFileMenuPosition] = React.useState<{
    mouseX: null | number;
    mouseY: null | number;
  }>(initialState);
  const [currentFileContextMenuNode, setCurrentFileContextMenuNode] =
    useState<ReturnNodeDto>();
  const isMobile = useAppSelector((s) => s.general.isMobile);
  const { show } = usePopup();

  const {
    isLoading: isLoadingNodes,
    data: nodes,
    refetch,
  } = useCustomQuery("files", () => fileApi.getNodes());
  const [order, setOrder] = React.useState<SortOrder>("asc");

  const { uploadProgress, uploadFiles } = useFileUpload({
    onSuccess: () => {
      refetch();
    },
  });

  useEffect(() => {
    const dragEnterListener = (evt: DragEvent) => {
      if (!evt.dataTransfer?.types.includes("Files")) return;
      setIsDraggingFile(true);
    };

    const dragEndListener = () => {
      setIsDraggingFile(false);
    };

    const dragLeaveListener = (evt: DragEvent) => {
      if (
        evt.clientX > 0 &&
        evt.clientX < window.document.body.clientWidth &&
        evt.clientY > 0 &&
        evt.clientY < window.document.body.clientHeight
      )
        return;
      setIsDraggingFile(false);
    };

    window.document.addEventListener("dragenter", dragEnterListener);
    window.document.addEventListener("dragleave", dragLeaveListener);
    window.document.addEventListener("drop", dragEndListener);

    return function cleanup() {
      window.document.removeEventListener("dragenter", dragEnterListener);
      window.document.removeEventListener("dragleave", dragLeaveListener);
      window.document.removeEventListener("drop", dragEndListener);
    };
  }, []);

  const handleNodeRightClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    node: ReturnNodeDto,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setContextTreeMenuPosition({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
    setCurrentTreeContextMenuNode(node);
  };

  const handleFileRightClick = (
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    node: ReturnNodeDto,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setContextFileMenuPosition({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
    setCurrentFileContextMenuNode(node);
  };

  const findNode = useCallback(
    (id: number, node?: ReturnNodeDto): ReturnNodeDto | null => {
      const currNode = node ?? (nodes?.nodes as ReturnNodeDto);
      if (!currNode) return null;

      if (currNode.id === id) return currNode;

      if (!currNode.childNodes) return null;

      let foundNode: ReturnNodeDto | null = null;

      currNode.childNodes.forEach((n) => {
        const foundNode2 = findNode(id, n);
        if (foundNode2) foundNode = foundNode2;
      });

      return foundNode;
    },
    [nodes?.nodes],
  );

  const getAllParents = useCallback(
    (node: ReturnNodeDto, prevValues?: ReturnNodeDto[]): ReturnNodeDto[] => {
      const currValues = [node, ...(prevValues ?? [])];
      if (!node.parentId) return currValues;

      const parent = findNode(node.parentId);

      if (!parent) return currValues;

      return getAllParents(parent, currValues);
    },
    [findNode],
  );

  // Open explorer in specific context in some situations
  React.useEffect(() => {
    if (!context || !nodes) return;
    const openContext = (
      c: NodeContext,
      searchNodes: ReturnNodeDto[],
    ): void => {
      const foundNode = searchNodes.find((n) => {
        return n.id === c.id || n.name === c.name;
      });
      if (foundNode) {
        if ("child" in c) {
          openContext(c.child, foundNode.childNodes ?? []);
        } else {
          const id = foundNode.id.toString();
          setSelectedFolder(id);
          setNodesExpanded([
            ...getAllParents(foundNode).map((x) => x.id.toString()),
            id,
          ]);
        }
      }
    };
    openContext(context, nodes.nodes?.childNodes ?? []);
  }, [context, getAllParents, nodes]);

  const selectedNode = !selectedFolder
    ? null
    : findNode(parseInt(selectedFolder, 10));

  const [sortBy, setSortBy] = React.useState<keyof ReturnNodeDto>("name");

  const renderTreeItem = useCallback(
    (node: ReturnNodeDto | undefined) => {
      if (!node) return null;

      return (
        <TreeItem
          classes={{ group: classes.group }}
          key={node.id}
          nodeId={node.id.toString()}
          label={
            <div className={classes.labelRoot}>
              <FolderIcon className={classes.labelIcon} />
              <Typography variant="body2" noWrap className={classes.labelText}>
                {translateNodeName(node.name)}
              </Typography>
            </div>
          }
          title={translateNodeName(node.name)}
          onContextMenu={(e) => handleNodeRightClick(e, node)}
          style={{ cursor: "context-menu" }}
        >
          {node.childNodes?.filter((x) => x.isFolder).map(renderTreeItem)}
        </TreeItem>
      );
    },
    [
      classes.group,
      classes.labelIcon,
      classes.labelRoot,
      classes.labelText,
      translateNodeName,
    ],
  );

  const createNewFolder = useCallback(
    (node?: ReturnNodeDto | null) => {
      if (!node) return;
      show(
        <AddFolderPopupForm
          parent={node}
          onSubmitted={(createdNode) => {
            refetch();
            setSelectedFolder(createdNode.id.toString());
            setNodesExpanded([
              ...getAllParents(createdNode).map((x) => x.id.toString()),
              createdNode.id.toString(),
            ]);
          }}
        />,
        { noDialog: true },
      );
    },
    [getAllParents, refetch, show],
  );

  const selectNode = useCallback(
    (node: ReturnNodeDto) => {
      setSelectedFolder(node.id.toString());
      setNodesExpanded([
        ...getAllParents(node).map((x) => x.id.toString()),
        node.id.toString(),
      ]);
    },
    [getAllParents],
  );

  const formatSize = useCallback(
    (bytes: number, decimals: number): string => {
      if (bytes === 0) return `0 ${t("bytes")}`;

      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = [t("bytes"), t("kb"), t("mb"), t("gb"), t("tb")];

      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
    },
    [t],
  );
  if (isLoadingNodes) return <LoadingBox height="100%" />;

  return (
    <Box
      {...rest}
      sx={{
        borderRadius: { xs: 0, md: embedded ? 0 : 1 },
        maxHeight: "100%",
        height: { xs: "100%", md: embedded ? "100%" : "60vh" },
        width: embedded ? "100%" : undefined,
        border: embedded ? undefined : "1px solid #666",
      }}
    >
      <Grid
        container
        direction="column"
        wrap="nowrap"
        style={{ height: "100%", maxHeight: "100%" }}
      >
        <FileExplorerToolBar
          embedded={embedded}
          selectedNode={selectedNode}
          refetch={refetch}
          createNewFolder={createNewFolder}
          uploadFiles={uploadFiles}
          onClose={onClose}
        />
        <Box
          style={{
            flexGrow: 1,
            overflow: "hidden",
            display: "flex",
          }}
        >
          <Box
            sx={{
              resize: "horizontal",
              width: isMobile ? "150px" : "300px",
              minWidth: isMobile ? "75px" : "150px",
            }}
            className={classes.treeContainer}
          >
            <TreeView
              className={classes.root}
              defaultCollapseIcon={<ExpandMoreIcon />}
              defaultExpandIcon={<ChevronRightIcon />}
              selected={selectedFolder}
              expanded={nodesExpanded}
              onNodeSelect={(_: unknown, id: string) => {
                setSelectedFolder(id);
                setSelectedFiles([]);
              }}
              onNodeToggle={(_, ids: string[]) => setNodesExpanded(ids)}
            >
              {renderTreeItem(nodes?.nodes)}
            </TreeView>
            <FileExplorerTreeContextMenu
              currentContextMenuNode={currentTreeContextMenuNode}
              createFolder={createNewFolder}
              contextMenuPosition={contextTreeMenuPosition}
              handleClose={() => {
                setContextTreeMenuPosition(initialState);
                setCurrentTreeContextMenuNode(undefined);
              }}
            />
          </Box>

          <Box
            style={{
              flex: 1,
              minWidth: isMobile ? "75px" : "150px",
            }}
          >
            <Box
              height="100%"
              display="grid"
              gridTemplateRows={
                isMobile ? "24px auto min-content" : "24px auto 40px"
              }
              gridTemplateColumns="100%"
            >
              <Box className={classes.breadcrumbContainer}>
                <CustomBreadcrumbs
                  maxItems={3}
                  itemsBeforeCollapse={0}
                  itemsAfterCollapse={1}
                  aria-label="breadcrumb"
                >
                  {!selectedNode ? (
                    <Typography>{t("select-a-folder")}</Typography>
                  ) : (
                    getAllParents(selectedNode).map((node, index, arr) => (
                      <Typography
                        style={{
                          userSelect: "none",
                          cursor:
                            index === arr.length - 1 ? "default" : "pointer",
                        }}
                        className={
                          index === arr.length - 1 ? "" : classes.breadcrumb
                        }
                        key={node.id}
                        color="textPrimary"
                        noWrap
                        onClick={() => {
                          selectNode(node);
                          setSelectedFiles([]);
                        }}
                      >
                        {translateNodeName(node.name)}
                      </Typography>
                    ))
                  )}
                </CustomBreadcrumbs>
              </Box>
              <Box
                className={
                  isMobile ? classes.nodeContainerMobile : classes.nodeContainer
                }
              >
                <FileExplorerFileContextMenu
                  currentContextMenuNode={currentFileContextMenuNode}
                  refetch={refetch}
                  contextMenuPosition={contextFileMenuPosition}
                  handleClose={() => {
                    setContextFileMenuPosition(initialState);
                    setCurrentFileContextMenuNode(undefined);
                  }}
                />
                <Choose>
                  <Choose.When
                    condition={
                      !!uploadProgress ||
                      (isDraggingFile &&
                        !!selectedNode &&
                        !selectedNode.programManaged)
                    }
                    render={() => (
                      <FileUpload
                        options={{
                          onDrop: (x) => uploadFiles(selectedNode!.id, x),
                        }}
                        uploadProgress={uploadProgress}
                      />
                    )}
                  />
                  <Choose.When
                    condition={!!selectedNode}
                    render={() => (
                      <Table
                        stickyHeader
                        size="small"
                        style={{ tableLayout: "fixed" }}
                      >
                        <FileExplorerTableHead<ReturnNodeDto>
                          setOrder={setOrder}
                          setSortBy={setSortBy}
                          tableHeaders={tableHeaders}
                          sortBy={sortBy}
                          order={order}
                        />

                        <TableBody>
                          {selectedNode?.childNodes
                            ?.slice()
                            .sort(getComparator<ReturnNodeDto>(order, sortBy))
                            .map((node) => (
                              <TableRow
                                key={node.id}
                                onContextMenu={(e) =>
                                  handleFileRightClick(e, node)
                                }
                                className={classNames(
                                  classes.node,
                                  selectedFiles.includes(node.id)
                                    ? classes.selectedItem
                                    : "",
                                )}
                                onClick={() => {
                                  if (node.isFolder) {
                                    selectNode(node);
                                    setSelectedFiles([]);
                                  } else if (onFileSelected) {
                                    if (selectedFiles.includes(node.id)) {
                                      setSelectedFiles((prev) =>
                                        prev.filter((x) => x !== node.id),
                                      );
                                    } else {
                                      setSelectedFiles((prev) =>
                                        disableMultiple
                                          ? [node.id]
                                          : [...prev, node.id],
                                      );
                                    }
                                  }
                                }}
                              >
                                <TableCell padding="none">
                                  <div
                                    className={classes.labelRoot}
                                    style={{ paddingLeft: "6px" }}
                                  >
                                    {node.isFolder ? (
                                      <FolderIcon
                                        className={classes.labelIcon}
                                      />
                                    ) : (
                                      <>
                                        {onFileSelected && (
                                          <Checkbox
                                            color="primary"
                                            checked={selectedFiles.includes(
                                              node.id,
                                            )}
                                            size="small"
                                            style={{
                                              padding: 0,
                                              paddingRight: 3,
                                            }}
                                          />
                                        )}
                                        <InsertDriveFileIcon
                                          className={classes.labelIcon}
                                        />
                                      </>
                                    )}
                                    <Typography
                                      variant="body2"
                                      noWrap
                                      title={translateNodeName(node.fullName)}
                                      className={classes.labelText}
                                    >
                                      {translateNodeName(node.fullName)}
                                    </Typography>
                                  </div>
                                </TableCell>
                                <TableCell
                                  padding="none"
                                  style={{ paddingRight: "12px" }}
                                  align="right"
                                >
                                  {node.isFolder
                                    ? null
                                    : formatSize(node.bytes, 2)}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    )}
                  />
                </Choose>
              </Box>
              {onFileSelected && (
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  sx={{ overflow: "hidden" }}
                >
                  <Grid
                    container
                    sx={{ textAlign: "center" }}
                    className={classNames(classes.selectedIndicator, {
                      [classes.selectedIndicatorHidden]: !selectedFiles?.length,
                    })}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Grid item>
                      <Typography>
                        {selectedFiles.length === 1
                          ? `${t("file-selected")} (${formatSize(
                              selectedFiles?.reduce((acc, cur) => {
                                const node = selectedNode?.childNodes?.find(
                                  (c) => c.id === cur,
                                );
                                if (!node) return 0;
                                return acc + node.bytes;
                              }, 0),
                              2,
                            )})`
                          : `${t("files-selected", {
                              count: selectedFiles.length,
                            })} (${formatSize(
                              selectedFiles?.reduce((acc, cur) => {
                                const node = selectedNode?.childNodes?.find(
                                  (c) => c.id === cur,
                                );
                                if (!node) return 0;
                                return acc + node.bytes;
                              }, 0),
                              2,
                            )})`}
                      </Typography>
                    </Grid>
                    {disableMultiple ? null : (
                      <Grid item>
                        {selectedFiles.length !==
                        selectedNode?.childNodes?.filter((c) => !c.isFolder)
                          ?.length ? (
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            onClick={() => {
                              if (selectedNode?.childNodes?.length) {
                                setSelectedFiles(
                                  selectedNode.childNodes
                                    .filter((c) => !c.isFolder)
                                    .map((c) => c.id),
                                );
                              }
                            }}
                          >
                            {t("select-all")}
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            onClick={() => setSelectedFiles([])}
                          >
                            {t("deselect-all")}
                          </Button>
                        )}
                      </Grid>
                    )}
                  </Grid>
                  <Button
                    sx={{ marginTop: isMobile ? "auto" : 0 }}
                    disabled={!selectedFiles.length}
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={() => {
                      const selectedNodes: ReturnNodeDto[] = [];
                      selectedFiles.forEach((f) => {
                        const n = findNode(f);
                        if (n) selectedNodes.push(n);
                      });
                      if (selectedNodes.length) onFileSelected?.(selectedNodes);
                    }}
                  >
                    {t("select")}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Grid>
    </Box>
  );
};

export default FileExplorer;
